import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import Select from './Select';
import Modal from './Modal';
import {
  getSearchHistory,
  addToSearchHistory,
  clearSearchHistory,
  removeFromSearchHistory,
  getFilterPresets,
  saveFilterPreset,
  deleteFilterPreset,
  SEARCH_SECTIONS
} from '../../services/unifiedSearchService';

const UnifiedSearchBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSection, setSelectedSection] = useState('properties');
  const [showHistory, setShowHistory] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [filterPresets, setFilterPresets] = useState([]);
  const [showSavePresetModal, setShowSavePresetModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const searchRef = useRef(null);

  // Load search history and presets on mount
  useEffect(() => {
    setSearchHistory(getSearchHistory());
    setFilterPresets(getFilterPresets());
  }, []);

  // Auto-detect current section from route
  useEffect(() => {
    const currentPath = location?.pathname;
    const detectedSection = Object.values(SEARCH_SECTIONS)?.find(
      section => section?.route === currentPath
    );
    if (detectedSection) {
      setSelectedSection(detectedSection?.id);
    }
  }, [location?.pathname]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setShowHistory(false);
        setShowPresets(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (term = searchTerm) => {
    if (!term?.trim()) return;

    // Add to history
    const updatedHistory = addToSearchHistory(term, selectedSection);
    setSearchHistory(updatedHistory);

    // Navigate to section with search query
    const section = SEARCH_SECTIONS?.[selectedSection];
    navigate(`${section?.route}?search=${encodeURIComponent(term)}`);

    // Close dropdowns
    setShowHistory(false);
    setShowPresets(false);
  };

  const handleHistoryClick = (historyItem) => {
    setSearchTerm(historyItem?.term);
    setSelectedSection(historyItem?.section);
    handleSearch(historyItem?.term);
  };

  const handlePresetClick = (preset) => {
    setSelectedSection(preset?.section);
    const section = SEARCH_SECTIONS?.[preset?.section];
    
    // Navigate with preset filters
    const queryParams = new URLSearchParams(preset.filters)?.toString();
    navigate(`${section?.route}?${queryParams}`);
    
    setShowPresets(false);
  };

  const handleClearHistory = () => {
    clearSearchHistory();
    setSearchHistory([]);
  };

  const handleRemoveHistoryItem = (id, e) => {
    e?.stopPropagation();
    const updatedHistory = removeFromSearchHistory(id);
    setSearchHistory(updatedHistory);
  };

  const handleSavePreset = () => {
    if (!presetName?.trim()) return;

    // Get current filters from URL or default
    const currentFilters = { search: searchTerm };
    const updatedPresets = saveFilterPreset(presetName, selectedSection, currentFilters);
    setFilterPresets(updatedPresets);
    setPresetName('');
    setShowSavePresetModal(false);
  };

  const handleDeletePreset = (id, e) => {
    e?.stopPropagation();
    const updatedPresets = deleteFilterPreset(id);
    setFilterPresets(updatedPresets);
  };

  const sectionOptions = Object.values(SEARCH_SECTIONS)?.map(section => ({
    value: section?.id,
    label: section?.label
  }));

  const currentSectionPresets = filterPresets?.filter(
    preset => preset?.section === selectedSection
  );

  const recentSearches = searchHistory?.filter(item => item?.section === selectedSection)?.slice(0, 5);

  return (
    <>
      <div ref={searchRef} className="relative flex items-center gap-2 w-full max-w-2xl">
        {/* Section Selector */}
        <div className="hidden md:block w-40">
          <Select
            options={sectionOptions}
            value={selectedSection}
            onChange={setSelectedSection}
            placeholder="القسم"
            className="h-10 text-sm"
          />
        </div>

        {/* Search Input */}
        <div className="relative flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            onKeyDown={(e) => e?.key === 'Enter' && handleSearch()}
            onFocus={() => setShowHistory(true)}
            placeholder={`بحث في ${SEARCH_SECTIONS?.[selectedSection]?.label}...`}
            className="w-full h-10 px-4 pr-10 rounded-xl border-2 border-input bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-250"
          />
          <Icon
            name="Search"
            size={18}
            color="#DAA520"
            className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
          />

          {/* Search History Dropdown */}
          {showHistory && recentSearches?.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-warm-lg z-50 max-h-80 overflow-y-auto">
              <div className="p-3 border-b border-border flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground">عمليات البحث الأخيرة</span>
                <button
                  onClick={handleClearHistory}
                  className="text-xs text-muted-foreground hover:text-destructive transition-colors"
                >
                  مسح الكل
                </button>
              </div>
              {recentSearches?.map((item) => (
                <button
                  key={item?.id}
                  onClick={() => handleHistoryClick(item)}
                  className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-muted/50 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <Icon name="Clock" size={16} color="#B8860B" />
                    <span className="text-sm text-foreground">{item?.term}</span>
                  </div>
                  <button
                    onClick={(e) => handleRemoveHistoryItem(item?.id, e)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Icon name="X" size={14} color="#6B7280" />
                  </button>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Filter Presets Button */}
        <button
          onClick={() => setShowPresets(!showPresets)}
          className="relative h-10 px-3 rounded-xl border-2 border-input bg-background hover:bg-muted/50 transition-all duration-250 flex items-center gap-2"
          title="الفلاتر المحفوظة"
        >
          <Icon name="Star" size={18} color="#DAA520" />
          {currentSectionPresets?.length > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-primary text-black text-xs font-bold rounded-full flex items-center justify-center">
              {currentSectionPresets?.length}
            </span>
          )}
        </button>

        {/* Filter Presets Dropdown */}
        {showPresets && (
          <div className="absolute top-full right-0 mt-2 w-80 bg-card border border-border rounded-xl shadow-warm-lg z-50 max-h-96 overflow-y-auto">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground">الفلاتر المحفوظة</span>
              <Button
                size="xs"
                variant="ghost"
                iconName="Plus"
                onClick={() => setShowSavePresetModal(true)}
              >
                حفظ جديد
              </Button>
            </div>
            {currentSectionPresets?.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                لا توجد فلاتر محفوظة لهذا القسم
              </div>
            ) : (
              currentSectionPresets?.map((preset) => (
                <button
                  key={preset?.id}
                  onClick={() => handlePresetClick(preset)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors group border-b border-border last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <Icon name={preset?.icon} size={18} color="#DAA520" />
                    <div className="text-right">
                      <div className="text-sm font-semibold text-foreground">{preset?.name}</div>
                      {preset?.isCustom && (
                        <div className="text-xs text-muted-foreground">فلتر مخصص</div>
                      )}
                    </div>
                  </div>
                  {preset?.isCustom && (
                    <button
                      onClick={(e) => handleDeletePreset(preset?.id, e)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-destructive/10 rounded"
                    >
                      <Icon name="Trash2" size={14} color="#EF4444" />
                    </button>
                  )}
                </button>
              ))
            )}
          </div>
        )}

        {/* Search Button */}
        <Button
          onClick={() => handleSearch()}
          size="icon"
          className="h-10 w-10 hidden sm:flex"
          iconName="Search"
          title="بحث"
        />
      </div>
      {/* Save Preset Modal */}
      {showSavePresetModal && (
        <Modal
          isOpen={showSavePresetModal}
          onClose={() => setShowSavePresetModal(false)}
          title="حفظ فلتر جديد"
          size="sm"
          icon="Star"
          iconColor="#DAA520"
          footer={
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowSavePresetModal(false)}
              >
                إلغاء
              </Button>
              <Button
                onClick={handleSavePreset}
                disabled={!presetName?.trim()}
              >
                حفظ
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                اسم الفلتر
              </label>
              <input
                type="text"
                value={presetName}
                onChange={(e) => setPresetName(e?.target?.value)}
                onKeyDown={(e) => e?.key === 'Enter' && handleSavePreset()}
                placeholder="مثال: العقارات المفضلة"
                className="w-full h-11 px-4 rounded-xl border-2 border-input bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-250"
                autoFocus
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default UnifiedSearchBar;